/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { EnterpriseLearnerInsights_MetricTimeFrame } from '__generated__/graphql-types';

import { Button, ContextualHelp, Dialog, Grid, Skeleton, Typography2 } from '@coursera/cds-core';
import type { IconProps } from '@coursera/cds-core';

import LearningInsightsPercentageArrow from 'bundles/enterprise-admin-home/components/LearningInsightsPercentageArrow';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = {
  wrapper: css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-300);
  `,
  skeletonIcon: css`
    margin-right: var(--cds-spacing-200);
  `,
  iconWrapper: css`
    background-color: var(--cds-color-emphasis-primary-background-xweak);
    border-radius: var(--cds-border-radius-max);
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: var(--cds-spacing-200);
  `,
  rowWrapper: css`
    display: flex;
    flex-direction: row;
  `,
  metricNumber: css`
    margin-right: var(--cds-spacing-100);
  `,
  arrowRowWrapper: css`
    margin-bottom: 2px;
  `,
  updatedDailyText: css`
    margin-top: var(--cds-spacing-200);
  `,
};

type Props = {
  metricName: string;
  aboutMetricHeader: string;
  aboutMetricBody: React.ReactNode;
  icon: React.ComponentType<IconProps>;
  metricNum: number;
  metricMeasurement: string;
  percentageOfChange: number;
  timeframe: EnterpriseLearnerInsights_MetricTimeFrame;
  compact?: boolean;
};

const LearningInsightsRow = ({
  metricMeasurement,
  metricName,
  metricNum,
  aboutMetricHeader,
  aboutMetricBody,
  timeframe,
  percentageOfChange,
  icon,
  compact = false,
}: Props) => {
  const MetricIcon = icon;

  const getPreviousDaysText = () => {
    switch (timeframe) {
      case EnterpriseLearnerInsights_MetricTimeFrame.ThirtyDays:
        return _t('from previous 30 days');
      case EnterpriseLearnerInsights_MetricTimeFrame.SixtyDays:
        return _t('from previous 60 days');
      case EnterpriseLearnerInsights_MetricTimeFrame.NinetyDays:
        return _t('from previous 90 days');
      default:
        return '';
    }
  };

  return (
    <div css={styles.wrapper}>
      <div css={styles.rowWrapper}>
        <div css={styles.iconWrapper}>
          <MetricIcon size="large" />
        </div>
        <div css={{ flexGrow: 1 }}>
          <ContextualHelp
            aria-label={aboutMetricHeader}
            label={
              <Typography2 component="h4" variant="subtitleMedium">
                {metricName}
              </Typography2>
            }
            helpToggleProps={{ 'aria-label': aboutMetricHeader }}
          >
            {({ onClose, isExpanded = false }) => (
              <Dialog open={isExpanded} onClose={onClose}>
                <Dialog.HeadingGroup>{aboutMetricHeader}</Dialog.HeadingGroup>
                <Dialog.Content>
                  <React.Fragment>
                    {aboutMetricBody}
                    <Typography2 css={styles.updatedDailyText} component="p">
                      {_t('This data is updated daily.')}
                    </Typography2>
                  </React.Fragment>
                </Dialog.Content>
                <Dialog.Actions>
                  <Button onClick={onClose}>{_t('Got it')}</Button>
                </Dialog.Actions>
              </Dialog>
            )}
          </ContextualHelp>
          <Grid container spacing={compact ? 0 : 32} item alignItems="flex-end" xs={12} md={8} lg={12}>
            <Grid item>
              <Typography2 variant="subtitleLarge" component="span" css={styles.metricNumber}>
                {metricNum.toLocaleString()}
              </Typography2>
              <Typography2 color="supportText" component="span">
                {metricMeasurement}
              </Typography2>
            </Grid>
            <Grid
              item
              css={styles.arrowRowWrapper}
              data-testid="percentage-change-row"
              container
              alignItems="center"
              wrap="nowrap"
              xs={12}
              sm={6}
              md={compact ? 12 : 5}
            >
              <LearningInsightsPercentageArrow percentageOfChange={percentageOfChange} compact={compact} />
              <Typography2 component="span" color="supportText" css={{ whiteSpace: 'pre' }}>
                {getPreviousDaysText()}
              </Typography2>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export const LearningInsightsRowPlaceholder = () => {
  return (
    <div css={styles.wrapper} role="status">
      <div css={styles.rowWrapper}>
        <Skeleton variant="circle" width={48} height={48} css={styles.iconWrapper} />
        <div css={{ flexGrow: 1 }}>
          <Typography2 component="h4" variant="subtitleMedium" color="supportText">
            <Skeleton width={200} />
          </Typography2>
          <Grid
            container
            item
            alignItems="flex-end"
            justifyContent="space-between"
            xs={12}
            md={8}
            lg={12}
            direction="row"
          >
            <Grid item container alignItems="center" xs={3} wrap="nowrap">
              <Typography2 variant="titleSmall" component="span" css={styles.metricNumber}>
                <Skeleton width={150} />
              </Typography2>
              <Typography2 component="span">
                <Skeleton width={100} />
              </Typography2>
            </Grid>
            <Grid item css={styles.arrowRowWrapper}>
              <Typography2 component="span">
                <Skeleton width={200} />
              </Typography2>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default LearningInsightsRow;
